import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { CgChevronLeftO, CgChevronRightO, CgTrash, CgChevronDoubleLeftO, CgPen, CgAdd } from 'react-icons/cg'
import tw from 'twin.macro'
import { Button } from 'common/components/Button/Button'
import Workout from './Workout'
import {
  useListenDraftProgramQuery,
  useDeleteWorkoutMutation,
  useCleanWorkoutMutation,
  useListenWorkoutQuery,
  useAddExtraWorkoutToDayMutation,
  useDeleteMultiWorkoutMutation,
  useDeleteWktOfMultiWorkoutMutation,
} from '../programApi'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import { DayHeaderTitle } from './DayHeader'
import { DayHeaderContainer } from './styles'
import { largeViewWeekHeaderMinHeight, titleDescMinHeight } from '../constants/dayViewStyleConstants'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { RefsControlProvider } from 'common/components/RefsControl/WorkoutRefsControl/context'
import { useProgramDnd } from '../hooks/useProgramDnd'
import { DndContext, DragOverlay } from '@dnd-kit/core'
import { useDispatch, useSelector } from 'react-redux'
import { getPartsSelected, getPartsCopied } from '../programSlice'
import { ActionPanel } from './ActionPanel'
import { EmptyDayCard } from './EmptyDayCard'
import { PartDragOverlay } from './PartDragOverlay'
import { dropAnimation } from 'common/utils/dndUtils'
import { Spinner } from 'common/components/Spinner/Spinner'
import { getUpdateExerciseMutationState, getUpdateWktMutationState } from '../programCache'
import { debounce } from 'lodash'
import { WorkoutInfoForm } from '../WorkoutInfoForm/WorkoutInfoForm'
import { Dialog, DialogContent } from 'common/components/Dialog/Dialog'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { useEventListener } from 'common/hooks/useEventListener'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { updateTabRowsOnDelete } from 'modules/Layout/utils/layoutUtils'
import { ExerciseDragOverlay } from './exercise/ExerciseDragOverlay'
import { NEW_USER_QUIZ } from 'modules/App/components/Header'
import { setIdsOfResultsToDelete, setTitleOfContentToDelete } from 'modules/AppOnboardingQuiz/quizFlowSlice'
import useQuizResultExists from 'modules/AppOnboardingQuiz/hooks/useQuizResultExists'
import { removeItemsInCollections } from 'modules/Collections/collectionUtils'
import WorkoutDuration from './WorkoutDuration'
import { MultiWorkout } from './DayViewMultiWkt'
import { getOrdinalSuffix } from 'common/utils/numberUtils'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { MultiWktDragOverlay } from './MultiWktDragOverlay'

function DayLargeView() {
  const navigate = useNavigate()
  const { id: programId, workoutId } = useParams()
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const [editMetaModalIsOpen, setEditWorkoutInfoModalOpen] = useState(false)
  const [modalWorkout, setModalWorkout] = useState(null)
  const [selectedWktId, setSelectedWktId] = useState(null)

  const orgId = coachOrgId

  const { data: programData = {}, isLoading: programLoading } = useListenDraftProgramQuery({
    orgId,
    programId,
  })

  const { data: workout = {}, isLoading: workoutLoading } = useListenWorkoutQuery({
    orgId,
    workoutId,
  })

  const dataLoading = programLoading || programData.isFiller || workoutLoading || workout.stillLoading

  const { program = {}, wktIdsSorted = [], wktIdsToDayIdcs, wktIdToMultiWkts, shouldRedirect } = programData

  useEffect(() => {
    if (shouldRedirect) {
      navigate('/programs')
    }
  }, [shouldRedirect, navigate])

  const { partDragging, exDragging, multiWktDragging, sensors, handleDragStart, handleDragOver, handleDragEnd } =
    useProgramDnd({
      wktIdsToDayIdcs,
      wktIdToMultiWkts,
      multiWktDays: program?.multiWktDays,
      wktIdsSorted,
      programId,
      setEditIdx: () => null,
      orgId,
      isDayLargeView: true,
    })

  const partsSelected = useSelector(getPartsSelected)
  const partsCopied = useSelector(getPartsCopied)

  const [cleanWorkout] = useCleanWorkoutMutation()

  const showActionPanel = partsSelected.length > 0 || partsCopied.length > 0
  const titleRef = useRef()

  useEventListener('keydown', handleReturnToProgram)

  const multiWktIds = program?.multiWktDays?.[workout?.multiWktDayId]?.workouts || []

  return (
    <>
      <div className='flex px-16 pt-12 pb-[40vh] bg-offWhite min-h-screen'>
        <div className='flex flex-col w-full max-w-xl mx-auto'>
          {dataLoading ? (
            <DayLargeViewSkeleton />
          ) : (
            <div>
              <Button
                variant='secondary'
                size='md'
                className='relative group mx-auto'
                onClick={() => handleReturnToProgram(null)}
              >
                <Hint className='translate-x-1/4 group-hover:translate-x-0'>or hit escape key</Hint>
                <CgChevronDoubleLeftO className='w-5 h-5 mr-2' />
                Return to program
              </Button>
              <h1 className='text-center text-4xl font-bold text-tBlack mb-12 mt-4'>
                {program?.name || 'Untitled program'}
              </h1>
              <div className='flex flex-col'>
                <DayNavActions
                  handleChangePage={handleChangePage}
                  wktIdsSorted={wktIdsSorted}
                  currentWktDayIdx={workout.dayIdx}
                  orgId={orgId}
                />
                <div className='relative bg-white rounded-xl'>
                  <SavingIndicator />
                  <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    {Boolean(selectedWktId) || !multiWktIds.length ? (
                      <SingleWktView
                        setEditWorkoutInfoModalOpen={setEditWorkoutInfoModalOpen}
                        setModalWorkout={setModalWorkout}
                        orgId={orgId}
                        titleRef={titleRef}
                        multiWktIds={multiWktIds}
                        selectedWktId={!multiWktIds.length ? workoutId : selectedWktId}
                        setSelectedWktId={setSelectedWktId}
                      />
                    ) : (
                      <>
                        <DayHeader
                          workout={workout}
                          setEditWorkoutInfoModalOpen={setEditWorkoutInfoModalOpen}
                          setModalWorkout={setModalWorkout}
                          coachOrgId={orgId}
                          multiWktIds={multiWktIds}
                          selectedWktId={selectedWktId}
                          setSelectedWktId={setSelectedWktId}
                          mainWktId={workoutId}
                        />
                        <RefsControlProvider isEditing={true}>
                          <MultiWorkout
                            mainWktId={workoutId}
                            workoutIds={multiWktIds}
                            multiWktsTitle={program?.multiWktDays?.[workout?.multiWktDayId]?.title}
                            multiWktsInstructions={program?.multiWktDays?.[workout?.multiWktDayId]?.instructions}
                            setSelectedWktId={setSelectedWktId}
                            isEditing={true}
                            orgId={orgId}
                            titleRef={titleRef}
                            dayIdx={workout?.dayIdx}
                            multiWktDayId={workout?.multiWktDayId}
                          />
                        </RefsControlProvider>
                      </>
                    )}
                    {createPortal(
                      <DragOverlay zIndex={70} dropAnimation={dropAnimation} className='cursor-move'>
                        {partDragging && <PartDragOverlay partDragging={partDragging} />}
                        {exDragging && <ExerciseDragOverlay exerciseDragging={exDragging} />}
                        {multiWktDragging && <MultiWktDragOverlay multiWktDragging={multiWktDragging} orgId={orgId} />}
                      </DragOverlay>,
                      document.body
                    )}
                  </DndContext>
                </div>
              </div>
              <Dialog open={editMetaModalIsOpen} setOpen={setEditWorkoutInfoModalOpen}>
                <DialogContent header='Workout details'>
                  <FormRefsControlProvider>
                    <WorkoutInfoForm coachOrgId={coachOrgId} workout={modalWorkout} programId={programId} />
                  </FormRefsControlProvider>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>
      {showActionPanel && <ActionPanel partsSelected={partsSelected} partsCopied={partsCopied} />}
    </>
  )

  function handleReturnToProgram(e) {
    if (e === null || e.key === 'Escape') {
      // Escape belongs to a dialog open on top of the editor, not to the editor. Dismissing
      // something is not the same gesture as leaving the workout, and treating it as both
      // loses the coach their place mid-edit.
      //
      // The dialog component already stops the key travelling this far, but only when the
      // dialog is the layer Escape reaches. A tooltip inside the dialog is a dismissable
      // layer too, and the key goes to whichever layer opened last — so an open tooltip takes
      // it, the dialog's own handler never runs, and the key arrives here. Asking the
      // document whether a dialog is open covers both routes at once.
      //
      // Only Radix dialogs are matched: they carry data-state, which the two hand-written
      // role='dialog' elements in the exercise editor (the timer helper and the "/" command
      // dropdown) do not, and those should keep closing on Escape as they always have.
      // A null event is the "Return to program" button, which always means to navigate.
      if (e !== null && document.querySelector('[role="dialog"][data-state="open"]')) return

      if (!partDragging) {
        cleanWorkout({
          orgId,
          workoutId,
          workout,
        })
      }
      navigate(`/programs/${programId}`)
    }
  }

  function handleChangePage(wktId) {
    if (!partDragging) {
      cleanWorkout({
        orgId,
        workoutId,
        workout,
      })
      navigate(`/programs/${programId}/workouts/${wktId}`)
    }
  }
}

const Hint = tw.div`
  absolute
  left-0
  -top-6
  text-xs
  font-normal
  italic
  whitespace-nowrap
  opacity-0
  group-hover:opacity-100
  transition-all
`

function SingleWktView({
  setEditWorkoutInfoModalOpen,
  setModalWorkout,
  orgId,
  titleRef,
  multiWktIds,
  selectedWktId,
  setSelectedWktId,
}) {
  const { workoutId } = useParams()

  const { data: workout = {}, isLoading } = useListenWorkoutQuery({
    orgId,
    workoutId: selectedWktId,
  })
  const wktLoading = isLoading || workout.stillLoading

  if (wktLoading) {
    return <WorkoutSkeleton />
  }

  return (
    <>
      <DayHeader
        workout={workout}
        setEditWorkoutInfoModalOpen={setEditWorkoutInfoModalOpen}
        setModalWorkout={setModalWorkout}
        coachOrgId={orgId}
        multiWktIds={multiWktIds}
        selectedWktId={selectedWktId}
        setSelectedWktId={setSelectedWktId}
        mainWktId={workoutId}
      />
      <div className='min-h-[20rem]'>
        {workout.type === 'empty' ? (
          <EmptyDayCard workout={workout} selectedWktId={selectedWktId} alwaysShowActions={true} orgId={orgId} />
        ) : (
          <RefsControlProvider isEditing={true}>
            <Workout workout={workout} isEditing={true} orgId={orgId} titleRef={titleRef} />
          </RefsControlProvider>
        )}
      </div>
    </>
  )
}

function DayLargeViewSkeleton() {
  return (
    <div className='mt-20'>
      <Skeleton css={tw`mb-28 w-1/2 mx-auto`} />
      <WorkoutSkeleton />
    </div>
  )
}

function WorkoutSkeleton() {
  return (
    <>
      <DayHeaderContainer css={tw`rounded-t-xl`} style={{ minHeight: largeViewWeekHeaderMinHeight }}>
        <Skeleton css={tw`w-10 mt-2`} />
      </DayHeaderContainer>
      <div className='h-screen bg-white rounded-b-xl'>
        <div
          className='flex flex-col justify-center p-4 border-b border-gray-200'
          style={{ minHeight: titleDescMinHeight }}
        >
          <Skeleton css={tw`w-3/6 mb-6`} />
          <Skeleton css={tw`w-5/6`} />
        </div>
        <div className='h-full' />
      </div>
    </>
  )
}

function DayNavActions(props) {
  const { handleChangePage, wktIdsSorted, currentWktDayIdx } = props

  const prevWktId = wktIdsSorted[currentWktDayIdx - 1]
  const nextWktId = wktIdsSorted[currentWktDayIdx + 1]

  return (
    <div className='flex items-center justify-between mb-8'>
      <Button variant='secondary' size='md' disabled={!prevWktId} onClick={() => handleChangePage(prevWktId)}>
        <CgChevronLeftO className='w-5 h-5 mr-2' />
        Previous day
      </Button>
      <Button variant='secondary' size='md' disabled={!nextWktId} onClick={() => handleChangePage(nextWktId)}>
        Next day
        <CgChevronRightO className='w-5 h-5 ml-2' />
      </Button>
    </div>
  )
}

function DayHeader({
  workout,
  setEditWorkoutInfoModalOpen,
  setModalWorkout,
  coachOrgId,
  multiWktIds = [],
  selectedWktId,
  setSelectedWktId,
  mainWktId,
}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id: programId, workoutId } = useParams()
  const { createAlert } = useAlert()

  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [loading, setLoading] = useState(false)

  const [deleteWorkout] = useDeleteWorkoutMutation()
  const [deleteMultiWorkout] = useDeleteMultiWorkoutMutation()
  const [deleteWktOfMultiWorkout] = useDeleteWktOfMultiWorkoutMutation()
  const [addExtraWktToDay] = useAddExtraWorkoutToDayMutation()

  const shouldDeleteMultiWkt = Boolean(multiWktIds.length) && !selectedWktId

  const { quizResultExists, isQuizResultsLoading } = useQuizResultExists({ coachOrgId, itemId: workoutId })

  const switchToMultiWkts = useCallback(() => setSelectedWktId(null), [setSelectedWktId])

  const idxOfMultiWkt = multiWktIds.indexOf(selectedWktId)
  const deleteWktText =
    multiWktIds.length > 1 && selectedWktId ? `Delete workout ${idxOfMultiWkt + 1} of ${multiWktIds.length}` : 'Delete'

  if (!deleteConfirmation) {
    return (
      <DayHeaderTitle
        dayIdx={workout.dayIdx}
        containerClasses='!rounded-t-xl'
        titleClasses='!text-base'
        switchToMultiWkts={selectedWktId && multiWktIds.length > 1 ? switchToMultiWkts : null}
        isEditing={true}
        multiWktIdx={workout?.multiWktIdx}
        numMultiWkts={multiWktIds?.length}
      >
        <div className='absolute top-2 right-2 flex items-center'>
          {/* dont show for empty workouts and parent multi workouts */}
          {(workout.type === 'workout' || workout.type === 'single_video') && (!multiWktIds.length || selectedWktId) ? (
            <Tooltip content='Edit Details'>
              <Button
                size='md'
                variant='secondary'
                css={tw`bg-gray-50 hover:bg-tGreen hover:bg-opacity-10 hover:text-tGreen px-2 py-1`}
                onClick={() => {
                  setEditWorkoutInfoModalOpen(true)
                  setModalWorkout(workout)
                }}
              >
                <CgPen className='w-5 h-5' />
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip content={`Add ${getOrdinalSuffix(Math.max(multiWktIds.length + 1, 2))} workout`}>
            <Button
              size='md'
              variant='secondary'
              css={tw`bg-gray-50 hover:bg-tGreen hover:bg-opacity-10 hover:text-tGreen px-2 py-1`}
              onClick={() => {
                addExtraWktToDay({
                  orgId: coachOrgId,
                  programId,
                  dayIdx: workout?.dayIdx,
                  multiWktDayId: workout?.multiWktDayId,
                  mainWktId,
                })
                setSelectedWktId(null)
                createAlert({ text: `Workout added!`, type: 'success' })
              }}
            >
              <CgAdd className='w-5 h-5' />
            </Button>
          </Tooltip>
          <Tooltip content={deleteWktText}>
            <Button
              loading={isQuizResultsLoading}
              size='md'
              variant='secondary'
              css={tw`bg-gray-50 hover:bg-tRed hover:bg-opacity-10 hover:text-tRed px-2 py-1`}
              onClick={() => setDeleteConfirmation(true)}
            >
              <CgTrash className='w-5 h-5' />
            </Button>
          </Tooltip>
        </div>
        <div className='mt-1'>
          {workout?.type !== 'empty' ? (
            <WorkoutDuration
              workout={workout}
              orgId={coachOrgId}
              selectedWktId={selectedWktId}
              multiWtkIds={multiWktIds}
            />
          ) : (
            <div className='h-[26px]' />
          )}
        </div>
      </DayHeaderTitle>
    )
  }

  if (quizResultExists) {
    return (
      <DayHeaderTitle
        dayIdx={workout.dayIdx}
        containerClasses='!rounded-t-xl'
        titleClasses='!text-xs !font-normal !max-w-[50%]'
        titleText='This workout cannot be deleted because it is used as a result in the new user quiz. To delete the workout, please remove it from the quiz first.'
      >
        <div className='flex items-center'>
          <Button
            size='md'
            variant='secondary'
            css={tw`bg-gray-50 mx-3 py-2`}
            onClick={() => setDeleteConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            size='md'
            variant='primary'
            css={tw`py-2`}
            onClick={() => {
              dispatch(setIdsOfResultsToDelete({ itemIds: { [workoutId]: true } }))
              dispatch(setTitleOfContentToDelete(workout.title || 'Untitled Workout'))
              navigate(NEW_USER_QUIZ)
            }}
            loading={isQuizResultsLoading}
          >
            Go to quiz
          </Button>
        </div>
      </DayHeaderTitle>
    )
  }

  return (
    <DayHeaderTitle
      dayIdx={workout.dayIdx}
      containerClasses='!rounded-t-xl !flex-row !px-4'
      titleClasses='!text-base'
      titleText={shouldDeleteMultiWkt ? 'This will delete all workouts for this day.' : 'This will delete the workout.'}
    >
      <div className='flex items-center'>
        <Button
          size='md'
          variant='secondary'
          css={tw`bg-gray-50 mx-3 py-2`}
          onClick={() => setDeleteConfirmation(false)}
        >
          Cancel
        </Button>
        <Button size='md' variant='danger' css={tw`py-2`} onClick={handleWorkoutDelete} loading={loading}>
          Delete
        </Button>
      </div>
    </DayHeaderTitle>
  )

  async function handleWorkoutDelete() {
    setLoading(true)

    if (shouldDeleteMultiWkt) {
      const { data } = await deleteMultiWorkout({
        orgId: coachOrgId,
        programId,
        dayIdx: workout.dayIdx,
        multiWktDayId: workout?.multiWktDayId,
        workoutIds: multiWktIds,
        mainWktId,
      })
      await updateTabRowsOnDelete({ coachOrgId: coachOrgId, itemIds: multiWktIds })
      await removeItemsInCollections({ coachOrgId: coachOrgId, itemIds: multiWktIds })
      if (data?.newWktId) {
        navigate(`/programs/${programId}/workouts/${data?.newWktId}`, { replace: true })
      }
    } else if (selectedWktId) {
      const { data } = await deleteWktOfMultiWorkout({
        orgId: coachOrgId,
        programId,
        workout,
        workoutIds: multiWktIds,
        mainWktId,
      })
      await updateTabRowsOnDelete({ coachOrgId: coachOrgId, itemIds: [selectedWktId] })
      await removeItemsInCollections({ coachOrgId: coachOrgId, itemIds: [selectedWktId] })
      setDeleteConfirmation(false)
      setSelectedWktId(null)
      if (data?.newMainWktId) {
        navigate(`/programs/${programId}/workouts/${data?.newMainWktId}`, { replace: true })
      }
    } else {
      await deleteWorkout({ orgId: coachOrgId, workoutId, programId, workout, dayIdx: workout.dayIdx })
      await updateTabRowsOnDelete({ coachOrgId: coachOrgId, itemIds: [workoutId] })
      await removeItemsInCollections({ coachOrgId: coachOrgId, itemIds: [workoutId] })
    }

    setLoading(false)
  }
}

function SavingIndicator() {
  const [saving, setSaving] = useState(false)

  const { isLoading: isLoadingWktsMutation } = useSelector(getUpdateWktMutationState)
  const { isLoading: isLoadingExercMutation } = useSelector(getUpdateExerciseMutationState)

  // Needed because mutations can resolve too quickly.
  // Thus, saving indicator would unmount too quickly and user might not see the indicator.
  useEffect(() => {
    const debouncedSetSaving = debounce(setSaving, 1000)

    if (isLoadingWktsMutation || isLoadingExercMutation) {
      setSaving(true)
    } else if (!isLoadingWktsMutation && !isLoadingExercMutation) {
      debouncedSetSaving(false)
    }

    return () => debouncedSetSaving.cancel()
  }, [isLoadingWktsMutation, isLoadingExercMutation])

  if (saving) {
    return (
      <div className='absolute -left-6 top-0 z-50'>
        <div className='fixed flex items-center bg-white px-4 py-2 rounded-lg text-sm text-tBlack -translate-x-full'>
          <Spinner /> Saving...
        </div>
      </div>
    )
  }

  return null
}

export default DayLargeView
